import { AlertCircle, CalendarDays, CreditCard, MapPin, Wallet } from 'lucide-react';
import {
  bookingTotal,
  lineTotalForParticipant,
  type AvailabilitySlot,
  type ExperienceOption,
  type PaymentMethod,
} from '@flyingmuse/shared';
import type { ContactDraft } from '../bookingFlow';
import { formatDateLong, formatPrice, formatTimeLabel } from '../format';

interface Props {
  experienceTitle: string;
  option: ExperienceOption;
  slot: AvailabilitySlot;
  contact: ContactDraft;
  comments: string;
  quantities: Record<string, number>;
  /** Methods on offer. With a single entry the chooser is hidden. */
  methods: PaymentMethod[];
  paymentMethod: PaymentMethod;
  onPaymentMethod: (method: PaymentMethod) => void;
  error: string | null;
}

export function ReviewStep({
  experienceTitle,
  option,
  slot,
  contact,
  comments,
  quantities,
  methods,
  paymentMethod,
  onPaymentMethod,
  error,
}: Props) {
  const time = formatTimeLabel(slot.dateTime);
  const lines = option.participantTypes
    .map((pt) => ({ pt, qty: quantities[pt.alias] ?? 0 }))
    .filter((l) => l.qty > 0)
    .map((l) => ({
      name: l.pt.name,
      qty: l.qty,
      total: lineTotalForParticipant(
        slot.pricing.find((p) => p.participantTypeAlias === l.pt.alias),
        l.qty,
      ),
    }));
  const grandTotal = bookingTotal(slot, quantities);

  return (
    <div>
      <h3 className="font-display text-2xl font-medium text-charcoal">
        Review &amp; confirm
      </h3>

      <div className="mt-5 rounded-xl border border-charcoal/15 bg-cream-soft p-5">
        <p className="font-display text-lg font-medium text-charcoal">
          {experienceTitle}
        </p>
        <p className="font-sans text-sm font-light text-stone">{option.title}</p>

        <div className="mt-4 flex flex-col gap-2.5">
          <p className="flex items-center gap-2.5 font-sans text-sm text-charcoal/85">
            <CalendarDays className="h-4 w-4 text-crimson" strokeWidth={1.8} />
            {formatDateLong(slot.dateTime)}
            {time ? ` · ${time}` : ''}
          </p>
          <p className="flex items-center gap-2.5 font-sans text-sm text-charcoal/85">
            <MapPin className="h-4 w-4 text-crimson" strokeWidth={1.8} />
            Fira, Santorini — pick-up details confirmed by message
          </p>
        </div>
      </div>

      {/* Line items */}
      <ul className="mt-4 flex flex-col divide-y divide-charcoal/10">
        {lines.map((l) => (
          <li key={l.name} className="flex items-center justify-between py-2.5">
            <span className="font-sans text-sm text-charcoal/85">
              {l.name} &times; {l.qty}
            </span>
            <span className="font-sans text-sm font-medium text-charcoal">
              {formatPrice(l.total)}
            </span>
          </li>
        ))}
        <li className="flex items-center justify-between pt-3">
          <span className="font-sans text-[0.7rem] font-medium uppercase tracking-wide2 text-charcoal/55">
            Total
          </span>
          <span className="font-display text-2xl font-medium text-charcoal">
            {formatPrice(grandTotal)}
          </span>
        </li>
      </ul>

      {/* Contact recap */}
      <div className="mt-4 rounded-xl border border-charcoal/10 bg-white p-4">
        <p className="font-sans text-sm font-medium text-charcoal">
          {contact.fullName}
        </p>
        <p className="font-sans text-xs font-light text-stone">
          {contact.email} · {contact.phone}
        </p>
        {comments.trim() && (
          <p className="mt-2 font-sans text-xs font-light italic text-stone">
            &ldquo;{comments.trim()}&rdquo;
          </p>
        )}
      </div>

      {/* Payment method — only shown when there is actually a choice to make. */}
      {methods.length > 1 && (
        <fieldset className="mt-5">
          <legend className="font-sans text-[0.62rem] font-semibold uppercase tracking-label text-charcoal/55">
            Payment
          </legend>
          <div className="mt-2.5 grid gap-2.5 sm:grid-cols-2">
            {methods.includes('arrival') && (
              <PaymentChoice
                selected={paymentMethod === 'arrival'}
                onSelect={() => onPaymentMethod('arrival')}
                icon={<Wallet className="h-4 w-4" strokeWidth={1.8} />}
                title="Pay on arrival"
                detail="Nothing charged today"
              />
            )}
            {methods.includes('card') && (
              <PaymentChoice
                selected={paymentMethod === 'card'}
                onSelect={() => onPaymentMethod('card')}
                icon={<CreditCard className="h-4 w-4" strokeWidth={1.8} />}
                title="Pay now by card"
                detail={`Secure checkout · ${formatPrice(grandTotal)}`}
              />
            )}
          </div>
        </fieldset>
      )}

      <p className="mt-4 rounded-lg bg-crimson/5 px-4 py-3 font-sans text-xs font-light text-charcoal/75">
        {paymentMethod === 'card' ? (
          <>
            <strong className="font-medium text-charcoal">
              Secure card payment.
            </strong>{' '}
            You&rsquo;ll be taken to Viva.com to pay {formatPrice(grandTotal)} — we
            never see your card details. Your booking is confirmed the moment the
            payment clears. Free to cancel per the studio&rsquo;s policy.
          </>
        ) : (
          <>
            <strong className="font-medium text-charcoal">
              Reserve now, pay on arrival.
            </strong>{' '}
            No payment is taken today — you&rsquo;ll settle in person on the day of
            your shoot. Free to cancel per the studio&rsquo;s policy.
          </>
        )}
      </p>

      {error && (
        <p className="mt-4 flex items-start gap-2 rounded-lg bg-crimson/10 px-4 py-3 font-sans text-sm text-crimson">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
}

function PaymentChoice({
  selected,
  onSelect,
  icon,
  title,
  detail,
}: {
  selected: boolean;
  onSelect: () => void;
  icon: React.ReactNode;
  title: string;
  detail: string;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={`flex items-start gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${
        selected
          ? 'border-crimson bg-crimson/5'
          : 'border-charcoal/15 bg-white hover:border-charcoal/30'
      }`}
    >
      <span
        className={`mt-0.5 shrink-0 ${selected ? 'text-crimson' : 'text-charcoal/45'}`}
      >
        {icon}
      </span>
      <span>
        <span className="block font-sans text-sm font-medium text-charcoal">
          {title}
        </span>
        <span className="block font-sans text-xs font-light text-stone">
          {detail}
        </span>
      </span>
    </button>
  );
}
